"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Sparkles, Heart, Activity, Ruler, Clock } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
interface BreedProfile {
  id: string;
  name: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
  size: string;
  coat: string;
  lifespan: string;
  temperament: string;
  clinicalNote: string;
  badge: string;
}
const catBreeds: BreedProfile[] = [{
  id: "maine-coon",
  name: "Maine Coon",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_portrait.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_portrait.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/maine_coon_cat_portrait.png-wvc-sizes",
  alt: "Maine Coon cat resting quietly on a light surface",
  size: "Large (11–25 lbs)",
  coat: "Dense, water-repellent semi-longhair",
  lifespan: "12–15 years",
  temperament: "Gentle, sociable, and attentive to family routines.",
  clinicalNote: "Routine screening for hypertrophic cardiomyopathy (HCM) and hip health is recommended by veterinarians.",
  badge: "Most popular large breed"
}, {
  id: "siamese",
  name: "Siamese",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_portrait.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_portrait.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/siamese_cat_portrait.png-wvc-sizes",
  alt: "Siamese cat with distinctive blue eyes and sleek coat",
  size: "Medium (8–12 lbs)",
  coat: "Short, sleek pointed coat with fine texture",
  lifespan: "14–18 years",
  temperament: "Vocal, curious, deeply bonded, and mentally active.",
  clinicalNote: "Prone to dental buildup; daily plaque management and annual cleanings support longevity.",
  badge: "High vocal communication"
}, {
  id: "british-shorthair",
  name: "British Shorthair",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_portrait.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_portrait.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/british_shorthair_cat_portrait.png-wvc-sizes",
  alt: "British Shorthair cat with plush blue-grey coat and amber eyes",
  size: "Medium to Large (9–17 lbs)",
  coat: "Plush, crisp double coat requiring weekly combing",
  lifespan: "12–16 years",
  temperament: "Calm, independent, quiet, and content with quiet spaces.",
  clinicalNote: "Weight management is vital as activity slows in adult years; track daily caloric intake.",
  badge: "Low-maintenance temperament"
}];
export default function BreedsSpotlight() {
  const prefersReduced = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: prefersReduced ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="cat-breeds-spotlight" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
              <Sparkles className="size-3.5 text-foreground" />
              <span>Breed profiles</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold tracking-tight text-foreground">
              Cat breeds spotlight
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground leading-relaxed">
              Compare temperament, grooming needs, and veterinary considerations to understand each breed before welcoming a new companion.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-sm font-semibold px-6 py-2.5 h-auto rounded-md">
              <Link to="/BreedsPage" className="inline-flex items-center gap-2">
                <span>Explore all cat breeds</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Breed Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pt-12">
          {catBreeds.map((breed, index) => <motion.div key={breed.id} variants={itemVariants} data-index={index}>
              <Card className="bg-card border-border rounded-xl overflow-hidden flex flex-col h-full shadow-xs hover:border-muted-foreground/40 hover:shadow-md transition-[border-color,box-shadow] duration-200">
                {/* Image Container with Editorial Aperture */}
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img src={breed.image} data-wvc-srcset={breed.srcset} data-wvc-sizes={breed.sizes} alt={breed.alt} loading="lazy" decoding="async" className="w-full h-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                  <div className="absolute top-3 left-3 bg-background/90 backdrop-blur-xs text-foreground px-2.5 py-1 rounded-md text-xs font-medium border border-border shadow-2xs">
                    {breed.badge}
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6 flex flex-col flex-grow justify-between gap-6">
                  <div>
                    {/* Header */}
                    <div className="flex items-baseline justify-between gap-2 mb-3">
                      <h3 className="text-2xl font-serif font-bold text-foreground">
                        {breed.name}
                      </h3>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-medium">
                        <Clock className="size-3.5" />
                        {breed.lifespan}
                      </span>
                    </div>

                    <p className="text-sm text-foreground/80 leading-relaxed mb-5">
                      {breed.temperament}
                    </p>

                    {/* Quick Specs Grid */}
                    <div className="grid grid-cols-2 gap-3 py-3 border-y border-border text-xs mb-5">
                      <div>
                        <span className="text-muted-foreground block mb-0.5">Average size</span>
                        <span className="font-semibold text-foreground flex items-center gap-1">
                          <Ruler className="size-3.5 text-muted-foreground" />
                          {breed.size}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground block mb-0.5">Coat type</span>
                        <span className="font-semibold text-foreground flex items-center gap-1">
                          <Activity className="size-3.5 text-muted-foreground" />
                          {breed.coat.split(",")[0]}
                        </span>
                      </div>
                    </div>

                    {/* Clinical Governance Callout */}
                    <div className="bg-background rounded-md p-3.5 border-l-2 border-l-foreground border border-border text-xs">
                      <span className="font-semibold text-foreground block mb-1 flex items-center gap-1.5">
                        <Heart className="size-3.5 text-foreground" />
                        Veterinary note
                      </span>
                      <p className="text-muted-foreground leading-relaxed">
                        {breed.clinicalNote}
                      </p>
                    </div>
                  </div>

                  {/* Footer Action */}
                  <div className="pt-2 border-t border-border/60">
                    <Button variant="outline" asChild className="w-full justify-between text-foreground border-border hover:bg-muted font-medium text-sm rounded-md">
                      <Link to="/BreedsPage">
                        <span>Read full breed profile</span>
                        <ArrowRight className="size-4 text-muted-foreground" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}